<script lang="ts">
import CoverageTypeFilter from '@/components/CoverageTypeFilter.vue';
import ManagedDateRange from '@/components/ManagedDateRange.vue';
import FavoriteIndicator from '@/components/FavoriteIndicator.vue';
import EmptyDisplay from '@/components/EmptyDisplay.vue';
import Checkbox from '@/components/Checkbox.vue';
import WholesalerGroupFilter from '@/components/WholesalerGroupFilter.vue';
import { Download, X, Search } from 'lucide-vue-next';
import { colorsBySourceAgencyKey } from '@/utilities/Color';
import { agencyLegendOrder } from '@/utilities/Charts';
import TextField from '@/components/v2/TextField.vue';
import AppModal from '@/components/v2/AppModal.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Skeleton from '@/components/v2/Skeleton.vue';
import VirtualList from '@/components/v2/VirtualList.vue';

export default {
  name: 'WholesalerGroupList',
  components: {
    FavoriteIndicator,
    EmptyDisplay,
    Checkbox,
    CoverageTypeFilter,
    ManagedDateRange,
    WholesalerGroupFilter,
    Download,
    TextField,
    AppModal,
    AppButton,
    Skeleton,
    VirtualList,
  },
};
</script>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue';
import { favoriteService, referenceService, wholesalerGroupService } from '@/services/api';
import { WholesalerGroup } from '@/types/WholesalerGroup';
import { stackedSeriesColors } from '@/utilities/Color';
import { CategoryValueSeriesWithSourceAgencyKey } from '@/types/label-value';
import { DateTime } from 'luxon';

interface WholesalerGroupTableRow extends WholesalerGroup {
  selected: boolean;
  description: string;
  isGroup: boolean;
  rowId: number | string;
}

// ------------
//     data
// ------------
const loading = ref(false);
const list = ref<WholesalerGroupTableRow[]>([]);
const filteredList = ref<WholesalerGroupTableRow[]>([]);
const coverageTypes = ref<any[]>([]);
const selectedCoverageTypes = ref([]);
const startDate = ref(DateTime.local().minus({ years: 1 }).toJSDate());
const endDate = ref(DateTime.local().toJSDate());
const wholesalers = ref<WholesalerGroup[]>([]);
const filteredWholesalerGroups = ref<WholesalerGroup[]>([]);
const searchValue = ref('');

// wholesaler group selection related
const selectionModeOn = ref(false);
const showDialog = ref(false);
const showDeleteDialog = ref(false);
const selectedGroupSelectionId = ref<undefined | number>(undefined);
const selectedWholesalerGroups = ref<Set<number | string>>(new Set());
const wholesalerGroupSelectionName = ref('');
const wholesalerGroupSelectionNameDefault = ref('');

const wholesalerPremiumData = ref<CategoryValueSeriesWithSourceAgencyKey[]>([]);
const premiumByWholesalerChart = {
  animation: '1',
  caption: 'Total Premium by Wholesaler',
  xAxisName: 'Wholesaler',
  yAxisName: 'Premium',
  theme: 'cacs',
  exportFilename: '',
  paletteColors: stackedSeriesColors,
  showSum: true,
  showValues: false,
  forceYAxisValueDecimals: false,
  decimals: 1,
};

const wholesalerPremiumDataSource = computed(() => {
  let data = wholesalerPremiumData.value;
  if (!data) return { chart: premiumByWholesalerChart };

  let wholesalers = Array.from(
    data
      .reduce((acc, result) => {
        const sum = (acc.get(result.category) || 0) + result.value;
        acc.set(result.category, sum);
        return acc;
      }, new Map())
      .entries(),
  )
    .sort((a, b) => b[1] - a[1])
    .map((entry) => ({ label: entry[0] }));

  let valuesWithIndex = data.map((r) => ({
    value: r.value,
    categoryIndex: wholesalers.findIndex((c) => c.label === r.category),
    seriesName: r.series,
  }));

  const agencies = new Map<string, { name: string; key: string }>();
  data.forEach((d) => {
    if (!agencies.has(d.series)) {
      agencies.set(d.series, { name: d.series, key: d.sourceAgencyKey });
    }
  });

  const sortedAgencies = Array.from(agencies.values()).sort((a, b) => (agencyLegendOrder.indexOf(a.key) > agencyLegendOrder.indexOf(b.key) ? 1 : -1));

  let series = sortedAgencies.map((agency) => ({
    seriesname: agency.name,
    color: colorsBySourceAgencyKey.get(agency.key),
    data: valuesWithIndex.reduce(
      (acc, cur) => {
        if (cur.seriesName === agency.name) {
          acc[cur.categoryIndex] = { value: cur.value };
        }
        return acc;
      },
      [] as { value: number }[],
    ),
  }));

  // fill holes in sparse array
  series.forEach((s) => {
    for (let i = 0; i < wholesalers.length; i++) {
      if (!s.data[i]) {
        s.data[i] = { value: 0 };
      }
    }
  });

  let result = { categories: [{ category: wholesalers }], dataset: series };
  return { chart: premiumByWholesalerChart, ...result };
});

// ------------
//   functions
// ------------

async function refresh(): Promise<void> {
  loading.value = true;

  const [groups, selections, coverages] = await Promise.all([
    wholesalerGroupService.list(),
    wholesalerGroupService.wholesalerGroupSelections(),
    loadCoverageTypes(),
  ]);

  wholesalers.value = groups;
  coverageTypes.value = coverages;

  list.value = (selections || [])
    .sort((a, b) => a.groupName.localeCompare(b.groupName))
    .map(
      (s) =>
        ({
          id: s.groupId, // todo make this an optional string
          name: s.groupName,
          selected: false,
          isGroup: true,
          rowId: `s-${s.groupId}`,
          description: s.wholesalers.map((w) => w.wholesalerGroupName).join(', '),
        }) as WholesalerGroupTableRow,
    );

  list.value.push(
    ...(groups || []).map((wg) => ({
      ...wg,
      selected: false,
      description: '',
      isGroup: false,
      rowId: wg.id,
    })),
  );
  filteredList.value = [...list.value];

  await refreshPremiumData();

  resetSelection();
  loading.value = false;
}

async function refreshPremiumData() {
  wholesalerPremiumData.value = await loadWholesalerPremiums();
}

async function loadCoverageTypes() {
  return (await referenceService.coverageTypes())
    .filter((item) => item.label.toLowerCase() !== 'fees')
    .map((ct) => ({ text: ct.label, value: ct.label }));
}

async function loadWholesalerPremiums() {
  const [start, end] = [DateTime.fromJSDate(startDate.value), DateTime.fromJSDate(endDate.value)];
  const wholesalerIds = filteredWholesalerGroups.value.map((wg) => wg.id);
  return await wholesalerGroupService.wholesalerPremiums(start, end, 25, selectedCoverageTypes.value, wholesalerIds);
}

// provide a suggested group named based on the first selection
function wholesalerGroupSelected(modelId: number | string): void {
  // user has toggled the checkbox off if value is in the set
  if (selectedWholesalerGroups.value.has(modelId)) {
    selectedWholesalerGroups.value.delete(modelId);
  } else {
    selectedWholesalerGroups.value.add(modelId);
  }
}

function search(value: string | number): void {
  const searchStr = typeof value === 'string' ? value : String(value);
  if (!searchStr) {
    filteredList.value = [...list.value];
    return;
  }
  filteredList.value = list.value.filter((wg) => wg.name.toLocaleLowerCase().includes(searchStr.toLocaleLowerCase()));
}

function updateListProgress(list: WholesalerGroupTableRow[], id: number, progress: boolean): WholesalerGroupTableRow[] {
  return list.map((w) => (w.id === id ? { ...w, progress } : w));
}

function updateListFavorite(list: WholesalerGroupTableRow[], id: number, favorite: boolean): WholesalerGroupTableRow[] {
  return list.map((w) => (w.id === id ? { ...w, favorite } : w));
}

async function toggleFavorite(wholesalerGroup: WholesalerGroup): Promise<void> {
  list.value = updateListProgress(list.value, wholesalerGroup.id, true);
  if (!wholesalerGroup.favorite) {
    await favoriteService.remove('wholesalerGroup', wholesalerGroup.id);
    list.value = updateListFavorite(list.value, wholesalerGroup.id, false);
    filteredList.value = updateListFavorite(filteredList.value, wholesalerGroup.id, false);
  } else {
    await favoriteService.create('wholesalerGroup', wholesalerGroup.id);
    list.value = updateListFavorite(list.value, wholesalerGroup.id, true);
    filteredList.value = updateListFavorite(filteredList.value, wholesalerGroup.id, true);
  }
  list.value = updateListProgress(list.value, wholesalerGroup.id, false);
  filteredList.value = updateListProgress(filteredList.value, wholesalerGroup.id, false);
}

function resetSelection(): void {
  list.value.forEach((wg) => (wg.selected = false));
  selectionModeOn.value = false;
  selectedWholesalerGroups.value.clear();
  wholesalerGroupSelectionName.value = '';
  wholesalerGroupSelectionNameDefault.value = '';
}

async function exportWholesalerPremiums() {
  const [start, end] = [DateTime.fromJSDate(startDate.value), DateTime.fromJSDate(endDate.value)];
  const wholesalerIds = filteredWholesalerGroups.value.map((wg) => wg.id);
  const data = await wholesalerGroupService.wholesalerPremiums(start, end, 0, selectedCoverageTypes.value, wholesalerIds);

  const seriesNames = Array.from(new Set(data.map((d) => d.series)));
  const header = ['Wholesaler', ...seriesNames].join(',');

  const premiumsMap = new Map();
  data.forEach((d) => {
    if (!premiumsMap.has(d.category)) {
      premiumsMap.set(d.category, {});
    }
    premiumsMap.get(d.category)[d.series] = d.value;
  });

  const csvRows = [header];
  premiumsMap.forEach((seriesData, wholesaler) => {
    const escapedWholesaler = wholesaler.includes(',') ? `"${wholesaler}"` : wholesaler;
    const row = [escapedWholesaler];
    seriesNames.forEach((series) => {
      row.push(seriesData[series] ? `$${seriesData[series].toFixed(2)}` : '$0.00');
    });
    csvRows.push(row.join(','));
  });

  const csvContent = csvRows.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'Total_Premium_By_Wholesaler.csv';
  a.click();
}

async function saveGroup(): Promise<void> {
  const wholesalerGroupIds = list.value.filter((wg) => wg.selected).map((wg) => wg.id);
  await wholesalerGroupService.createWholesalerGroupSelection(wholesalerGroupSelectionName.value, wholesalerGroupIds);
  showDialog.value = false;
  await refresh();
}

function initDialog(): void {
  // if the user changed the defaulted value, use that instead of generating a new one

  if (!wholesalerGroupSelectionName.value || wholesalerGroupSelectionName.value === wholesalerGroupSelectionNameDefault.value) {
    const wgId: string | number | undefined = selectedWholesalerGroups.value.values().next().value;
    const wgName = list.value.find((wg) => wg.id === wgId)?.name || '';

    if (selectedWholesalerGroups.value.size > 0) {
      wholesalerGroupSelectionName.value = `${wgName} Group`;
      wholesalerGroupSelectionNameDefault.value = `${wgName} Group`;
    }
  }

  showDialog.value = true;
}

function deleteGroupSelection(): void {
  wholesalerGroupService.deleteWholesalerGroupSelection(selectedGroupSelectionId.value as number);

  let idx = list.value.findIndex((wg) => wg.id === selectedGroupSelectionId.value);
  if (idx > -1) {
    list.value.splice(idx, 1);
  }

  idx = filteredList.value.findIndex((wg) => wg.id === selectedGroupSelectionId.value);
  if (idx > -1) {
    filteredList.value.splice(idx, 1);
  }

  selectedGroupSelectionId.value = undefined;
  showDeleteDialog.value = false;
}

function nameForGroupSelection(id: number | undefined): string {
  if (!id) return 'Unknown';
  return list.value.find((wg) => wg.id === id)?.name || '';
}

function setDate(datePayload) {
  if (datePayload.startDate) {
    startDate.value = datePayload.startDate;
  }
  if (datePayload.endDate) {
    endDate.value = datePayload.endDate;
  }
  refreshPremiumData();
}

function asRow(item: unknown): WholesalerGroupTableRow {
  return item as WholesalerGroupTableRow;
}

// ------------
// lifecycles
// ------------

onBeforeMount(async () => {
  await refresh();
});
</script>

<template>
  <div class="inner tw:h-full">
    <EmptyDisplay
      v-if="!loading && list.length === 0"
      title="No wholesalers"
      text="Unable to find any wholesalers." />

    <div class="tw:grid tw:grid-cols-1 tw:items-center tw:gap-4 tw:md:grid-cols-12">
      <div class="tw:md:col-span-8">
        <h1 class="tw:pr-1">Wholesalers</h1>
      </div>
      <div class="tw:min-w-0 tw:md:col-span-4">
        <TextField
          v-model="searchValue"
          :appendIcon="Search"
          data-test="wholesaler-search"
          placeholder="Search Wholesalers"
          @update:model-value="search" />
      </div>
    </div>

    <!-- Group creation dialog -->

    <AppModal
      :show="showDialog"
      title="Enter a Group Name"
      cancel-button-label="Cancel"
      action-button-label="Create Group"
      :handle-cancel="
        () => {
          showDialog = false;
        }
      "
      :handle-action="saveGroup"
      :action-disabled="(wholesalerGroupSelectionName || '').trim().length === 0"
      size="lg">
      <template #content>
        <TextField
          placeholder="Wholesaler Group Name"
          v-model="wholesalerGroupSelectionName" />
      </template>
    </AppModal>

    <!-- Group delete dialog -->

    <AppModal
      :show="showDeleteDialog"
      title="Delete Grouping"
      cancel-button-label="Cancel"
      action-button-label="Delete"
      :handle-cancel="
        () => {
          showDeleteDialog = false;
          selectedGroupSelectionId = undefined;
        }
      "
      :handle-action="deleteGroupSelection"
      size="lg">
      <template #content>
        <p>Are you sure you want to delete grouping "{{ nameForGroupSelection(selectedGroupSelectionId) }}"?</p>
      </template>
    </AppModal>

    <!-- Wholesaler Group table -->

    <div
      v-if="loading"
      class="tw:space-y-2 tw:mt-10">
      <Skeleton
        v-for="i in 8"
        :key="i"
        shape="rectangle"
        width="100%"
        height="36px" />
    </div>

    <div
      v-else
      class="tw:grid tw:grid-cols-1 tw:gap-4 tw:md:grid-cols-12">
      <div class="tw:md:col-span-5">
        <div class="tw:mt-6 tw:flex tw:flex-wrap tw:items-center tw:justify-end tw:gap-3">
          <div
            v-if="selectionModeOn"
            class="tw:flex tw:space-x-3">
            <AppButton @click="resetSelection">Cancel</AppButton>
            <AppButton
              primary
              :disabled="selectedWholesalerGroups.size === 0"
              @click="initDialog">
              Create Group
            </AppButton>
          </div>
          <div v-else>
            <AppButton
              primary
              :disabled="loading && list.length === 0"
              @click="selectionModeOn = true">
              View Multiple
            </AppButton>
          </div>
          <div
            data-tip="Export All as .CSV"
            class="tw:tooltip tw:tooltip-top tw:z-50 tw:flex">
            <AppButton @click="exportWholesalerPremiums">
              <Download
                class="tw:justify-self-center"
                :size="15" />
            </AppButton>
          </div>
        </div>

        <div
          class="tw:mt-2"
          v-if="!loading && list.length > 0">
          <div
            class="tw:m-0 tw:grid tw:h-10 tw:w-full tw:grid-cols-12 tw:gap-2 tw:border-t tw:border-gray-300 tw:bg-[#4D9CB9] tw:pl-2 tw:font-semibold tw:text-white tw:uppercase">
            <div
              v-if="selectionModeOn"
              class="tw:col-span-1">
              &nbsp;
            </div>
            <div class="tw:col-span-1">&nbsp;</div>
            <div class="tw:col-span-6 tw:flex tw:items-center tw:p-0">Name</div>
            <div
              :class="selectionModeOn ? 'tw:col-span-4' : 'tw:col-span-5'"
              class="tw:flex tw:items-center tw:px-0"></div>
          </div>

          <VirtualList
            :items="filteredList"
            :row-height="50">
            <template #row="{ item }">
              <div
                class="hover:tw:bg-gray-50 tw:group tw:grid tw:min-h-[50px] tw:w-full tw:grid-cols-12 tw:items-center tw:gap-2 tw:overflow-visible tw:border-t tw:border-gray-200 tw:pl-2"
                :data-test="`list-item-${asRow(item).rowId}`">
                <div
                  v-if="selectionModeOn"
                  class="tw:col-span-1 tw:flex tw:items-center tw:justify-start">
                  <Checkbox
                    v-if="!asRow(item).isGroup"
                    v-model="asRow(item).selected"
                    :model-id="asRow(item).id"
                    @model-id="wholesalerGroupSelected" />
                  <span v-else>&nbsp;</span>
                </div>
                <div class="tw:col-span-1 tw:flex tw:items-center tw:justify-start">
                  <FavoriteIndicator
                    v-if="!asRow(item).isGroup"
                    tooltip-position="right"
                    v-model="asRow(item).favorite"
                    @update:model-value="toggleFavorite(asRow(item))"
                    :loading="asRow(item).progress" />
                  <div
                    v-else
                    class="tw:invisible tw:w-4 tw:group-hover:visible">
                    <button
                      type="button"
                      @click="
                        showDeleteDialog = true;
                        selectedGroupSelectionId = asRow(item).id;
                      ">
                      <X :size="16" />
                    </button>
                  </div>
                </div>
                <div class="tw:col-span-6 tw:flex tw:min-w-0 tw:items-center">
                  <router-link
                    v-if="!asRow(item).isGroup"
                    class="tw:truncate tw:text-base tw:font-medium tw:tracking-wide tw:text-slate-700 tw:no-underline"
                    :to="`/wholesaler-group/${asRow(item).id}/overview`">
                    {{ asRow(item).name }}
                  </router-link>
                  <router-link
                    v-else
                    class="tw:truncate tw:text-base tw:font-medium tw:tracking-wide tw:text-slate-700 tw:no-underline"
                    :to="`/wholesaler-group/custom/${asRow(item).id}/overview`">
                    {{ asRow(item).name }}
                  </router-link>
                </div>
                <div
                  :class="selectionModeOn ? 'tw:col-span-4' : 'tw:col-span-5'"
                  class="tw:flex tw:min-w-0 tw:items-center tw:truncate tw:text-sm tw:text-gray-600">
                  {{ asRow(item).description }}
                </div>
              </div>
            </template>
          </VirtualList>
        </div>
      </div>

      <div class="tw:pl-2.5 tw:md:col-span-7">
        <div class="tw:mt-4">
          <div class="tw:my-2.5 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2">
            <CoverageTypeFilter
              :coverage-types="coverageTypes"
              v-model="selectedCoverageTypes"
              @update:model-value="refreshPremiumData" />
            <WholesalerGroupFilter
              class="tw:mr-auto tw:ml-2"
              @updated="refreshPremiumData"
              v-model:selected="filteredWholesalerGroups"
              :wholesalers="wholesalers" />

            <ManagedDateRange
              class="tw:justify-self-end"
              :initialStartDate="startDate"
              :initialEndDate="endDate"
              :onApply="setDate" />
          </div>

          <fusioncharts
            class="tw:mt-2"
            type="stackedbar2d"
            width="100%"
            height="680"
            dataFormat="json"
            :dataSource="wholesalerPremiumDataSource" />
        </div>
      </div>
    </div>
  </div>
</template>
